import { resolveField } from '/bb/identifier.js';
import { ensureCurrentPage, getOrCreateField, getOrCreateForm } from '/bb/helpers.js';
import { recordRangeValue } from '/bb/listeners/input.js';

const activeGestures = new Map();

function trackedRange(el) {
  const field = resolveField(el);
  return field && field.type === 'range' ? field : null;
}

function candidateRanges(event) {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];

  for (let i = 0; i < path.length; i++) {
    const node = path[i];
    if (!node || node.nodeType !== 1) continue;
    if (isFormBoundary(node)) break;

    if (trackedRange(node)) return [node];

    let current = node;
    let depth = 0;
    while (current && current.nodeType === 1 && depth < 12) {
      if (isFormBoundary(current)) break;

      if (typeof current.querySelectorAll === 'function') {
        const inputs = current.querySelectorAll('input[type="range"]');
        const candidates = [];
        for (let j = 0; j < inputs.length; j++) {
          if (trackedRange(inputs[j])) candidates.push(inputs[j]);
        }
        if (candidates.length > 0) return candidates;
      }

      current = current.parentElement;
      depth += 1;
    }
  }

  return [];
}

function isFormBoundary(node) {
  const tag = (node.tagName || '').toUpperCase();
  return (
    tag === 'FORM' || tag === 'BODY' || tag === 'HTML' || !!(node.hasAttribute && node.hasAttribute('data-rs-form'))
  );
}

function pointerKey(event) {
  const pointerType = event.pointerType || 'mouse';
  if (pointerType === 'mouse') return 'mouse';
  return event.pointerId == null ? pointerType : pointerType + ':' + event.pointerId;
}

function gestureStartHandler(event) {
  if (event.isPrimary === false) return;
  if ((event.pointerType || 'mouse') === 'mouse' && event.button !== 0) return;

  const key = pointerKey(event);
  const existing = activeGestures.get(key);
  if (existing) {
    scheduleObservation(existing, false);
    return;
  }

  const candidates = candidateRanges(event);
  if (candidates.length === 0) return;

  const values = new Map();
  const initialChangeCounts = new Map();
  const fieldAggs = new Map();
  const pageKey = ensureCurrentPage();
  for (let i = 0; i < candidates.length; i++) {
    const candidate = candidates[i];
    values.set(candidate, readValue(candidate));

    const field = trackedRange(candidate);
    if (!field) continue;
    const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
    fld.runtime.rangeGestureActive = true;
    fld.runtime.rangeGestureCounted = false;
    fld.runtime.rangeGestureChangeSeen = false;
    fld.runtime.rangeGestureEndedAt = 0;
    fld.runtime.rangeGestureCommittedValue = null;
    fieldAggs.set(candidate, fld);
    initialChangeCounts.set(candidate, fld.changes_count || 0);
  }

  const gesture = {
    key,
    candidates,
    input: candidates.length === 1 ? candidates[0] : null,
    values,
    initialChangeCounts,
    fieldAggs,
    counted: false,
    frame: 0,
    finishAfterFrame: false,
    cleanupTimer: 0,
  };

  activeGestures.set(key, gesture);
  scheduleObservation(gesture, false);
}

function gestureMoveHandler(event) {
  const gesture = activeGestures.get(pointerKey(event));
  if (gesture) scheduleObservation(gesture, false);
}

function gestureEndHandler(event) {
  const gesture = activeGestures.get(pointerKey(event));
  if (gesture) scheduleObservation(gesture, true);
}

function scheduleObservation(gesture, finish) {
  if (!gesture) return;
  if (finish) gesture.finishAfterFrame = true;
  if (gesture.frame) return;

  gesture.frame = requestFrame(() => {
    gesture.frame = 0;
    observeGesture(gesture);
    if (!gesture.finishAfterFrame) return;

    gesture.cleanupTimer = setTimeout(() => finishGesture(gesture), 0);
  });
}

function observeGesture(gesture) {
  const input = resolveGestureInput(gesture);
  if (!input) return;

  const value = readValue(input);
  const previousValue = gesture.values.get(input);
  gesture.values.set(input, value);
  gesture.input = input;

  const field = trackedRange(input);
  if (!field) return;

  const pageKey = ensureCurrentPage();
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);

  const initialChangeCount = gesture.initialChangeCounts.get(input) || 0;
  if ((fld.changes_count || 0) > initialChangeCount || fld.runtime.rangeGestureCounted) {
    gesture.counted = true;
    fld.runtime.rangeGestureCounted = true;
  }
  if (fld.runtime.rangeInputSeenSinceChange) gesture.counted = true;
  if (previousValue === value) return;

  const form = getOrCreateForm(pageKey, field.formId);
  const countChange = !gesture.counted && !fld.runtime.rangeInputSeenSinceChange;
  recordRangeValue(form, fld, field, input, countChange);
  gesture.counted = true;
  fld.runtime.rangeGestureCounted = true;
  fld.runtime.rangeGestureCommittedValue = value;

  // Suppress a native input/change pair emitted later in the same component gesture.
  // The flag is cleared when the gesture finishes if the component never emits change.
  fld.runtime.rangeInputSeenSinceChange = true;
}

function resolveGestureInput(gesture) {
  for (let i = 0; i < gesture.candidates.length; i++) {
    const candidate = gesture.candidates[i];
    if (candidate.isConnected && trackedRange(candidate) && gesture.values.get(candidate) !== readValue(candidate)) {
      return candidate;
    }
  }

  const active = typeof document !== 'undefined' ? document.activeElement : null;
  if (active && gesture.candidates.includes(active) && trackedRange(active)) return active;
  if (gesture.input && gesture.input.isConnected && trackedRange(gesture.input)) return gesture.input;

  for (let i = 0; i < gesture.candidates.length; i++) {
    const candidate = gesture.candidates[i];
    if (candidate.isConnected && trackedRange(candidate)) return candidate;
  }

  return null;
}

function finishGesture(gesture) {
  if (!gesture) return;
  if (gesture.frame) {
    cancelFrame(gesture.frame);
    gesture.frame = 0;
    observeGesture(gesture);
  }
  if (gesture.cleanupTimer) clearTimeout(gesture.cleanupTimer);

  const endedAt = Date.now();
  for (const fld of gesture.fieldAggs.values()) {
    fld.runtime.rangeGestureActive = false;
    if (fld.runtime.rangeGestureCounted && !fld.runtime.rangeGestureChangeSeen) {
      fld.runtime.rangeGestureEndedAt = endedAt;
    } else {
      fld.runtime.rangeGestureCounted = false;
      fld.runtime.rangeGestureEndedAt = 0;
      fld.runtime.rangeGestureCommittedValue = null;
    }
    fld.runtime.rangeInputSeenSinceChange = false;
  }

  if (activeGestures.get(gesture.key) === gesture) activeGestures.delete(gesture.key);
}

function readValue(input) {
  return input && typeof input.value === 'string' ? input.value : '';
}

function requestFrame(callback) {
  if (typeof requestAnimationFrame === 'function') return requestAnimationFrame(callback);
  return setTimeout(callback, 0);
}

function cancelFrame(handle) {
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(handle);
  else clearTimeout(handle);
}

let attached = false;

export function attach() {
  if (attached || typeof document === 'undefined') return;
  document.addEventListener('pointerdown', gestureStartHandler, { passive: true, capture: true });
  document.addEventListener('pointermove', gestureMoveHandler, { passive: true, capture: true });
  document.addEventListener('pointerup', gestureEndHandler, { passive: true, capture: true });
  document.addEventListener('pointercancel', gestureEndHandler, { passive: true, capture: true });
  document.addEventListener('mousedown', gestureStartHandler, { passive: true, capture: true });
  document.addEventListener('mousemove', gestureMoveHandler, { passive: true, capture: true });
  document.addEventListener('mouseup', gestureEndHandler, { passive: true, capture: true });
  attached = true;
}

export function detach() {
  if (!attached || typeof document === 'undefined') return;
  document.removeEventListener('pointerdown', gestureStartHandler, true);
  document.removeEventListener('pointermove', gestureMoveHandler, true);
  document.removeEventListener('pointerup', gestureEndHandler, true);
  document.removeEventListener('pointercancel', gestureEndHandler, true);
  document.removeEventListener('mousedown', gestureStartHandler, true);
  document.removeEventListener('mousemove', gestureMoveHandler, true);
  document.removeEventListener('mouseup', gestureEndHandler, true);
  for (const gesture of activeGestures.values()) finishGesture(gesture);
  activeGestures.clear();
  attached = false;
}
